import string from '@lib/string'
import http from '@app/http'
import pdfErrorDoc from './pdfErrorDoc'
import * as pdfjsLib from "pdfjs-dist/build/pdf";
import * as pdfjsViewer from 'pdfjs-dist/web/pdf_viewer.mjs'

// The pdf viewer css file must be imported. 
// However, we must add a scope around the content of the file because the styles conflict with our own styles. 
// We could just import the css file from within a scss file and wrap the content in a tag. 
// That does not work as the css contains styles which are invalid in scss. (e.g. --csstools-color-scheme--light:;)
// Long story short, since the image folder is required as well, copy the web folder to a local folder, make the fixes, 
// and import the css file from there. 
// 1. copy folder D:\development\optimit\v2\frontend\frontend.kantoor\app\node_modules\pdfjs-dist\web to 
//    D:\development\optimit\v2\frontend\frontend.shared\assets\css\pdfjs\5_4_624
// 2. Make rename the css file to a scss file 
// 3. Make fixes: 
//    - wrap the content in a tag, e.g. .pdfViewerContainer { ... }
//    - fix the invalid scss syntax, e.g. --csstools-color-scheme--light:; to // --csstools-color-scheme--light:;
// Instead of import "pdfjs-dist/web/pdf_viewer.css";
// : 
// import '@shared/assets/css/controls/pdf_viewer.scss' 
import '@shared/assets/css/pdfjs/5_4_624/web/pdf_viewer.scss' 

var CMAP_URL = "/dist/pdfjs-dist/cmaps/";
// UpdAte the following urlS when updating the pdfjs-dist version. IT MUST MATCH.
const CDN_CMAP_URL               = "https://cdn.jsdelivr.net/npm/pdfjs-dist@5.4.624/cmaps/"; 
const CDN_STANDARD_FONT_DATA_URL = "https://cdn.jsdelivr.net/npm/pdfjs-dist@5.4.624/standard_fonts/";
const CDN_WORKER_SRC             = "https://cdn.jsdelivr.net/npm/pdfjs-dist@5.4.624/build/pdf.worker.mjs"; // "pdfjs-dist/build/pdf.worker.mjs";

var CMAP_PACKED = true;
const TEXT_LAYER_MODE = 0; // DISABLE

const emptyDocument = string.base64Decode("JVBERi0xLjEKJeLjz9MKMSAwIG9iaiAKPDwKL1BhZ2VzIDIgMCBSCi9UeXBlIC9DYXRhbG9nCj4+CmVuZG9iaiAKMiAwIG9iaiAKPDwKL01lZGlhQm94IFswIDAgNTk1IDg0Ml0KL0tpZHMgWzMgMCBSXQovQ291bnQgMQovVHlwZSAvUGFnZXMKPj4KZW5kb2JqIAozIDAgb2JqIAo8PAovUGFyZW50IDIgMCBSCi9NZWRpYUJveCBbMCAwIDU5NSA4NDJdCi9UeXBlIC9QYWdlCj4+CmVuZG9iaiB4cmVmCjAgNAowMDAwMDAwMDAwIDY1NTM1IGYgCjAwMDAwMDAwMTUgMDAwMDAgbiAKMDAwMDAwMDA2NiAwMDAwMCBuIAowMDAwMDAwMTQ5IDAwMDAwIG4gCnRyYWlsZXIKCjw8Ci9Sb290IDEgMCBSCi9TaXplIDQKPj4Kc3RhcnR4cmVmCjIyMQolJUVPRgo=");

// Reference:  https://github.com/mozilla/pdf.js/blob/master/examples/mobile-viewer/viewer.mjs
// 
// Example implementation.
//     <div id="piViewerContainer" class="position-absolute container-viewer container-viewer-pdf pt-2" ref="container">
//         <div id="viewer" class="pdfViewer" align="center"></div>
//     </div>
//
//    import clsPdfjs from '@cls/clsPdfjs'
//    let container = ref();  // automagically loads the element with the container reference
//    let viewer = new clsPdfjs({callbackOnLoading: (value) => { console.log('isLoading, value);}});
//    
//    async function load() {        
//        await viewer.create(container.value);        
//        await viewer.loadBase64Data( props.model.pdfData() /* , fileName optional */);
//    }        
// 
// Note 1: 
// Call viewer.create in onMounted, or any other place where the DOM is already available. 
// Use document.addEventListener("DOMContentLoaded", ... when you need.
// 
// Note 2: 
// Always use await for loading and creation calls.
// 




// pdfjsLib.GlobalWorkerOptions.workerSrc = new URL("pdfjs-dist/build/pdf.worker.mjs", import.meta.url).toString();
pdfjsLib.GlobalWorkerOptions.workerSrc = CDN_WORKER_SRC;

class clsPdfjs {
    pdfLoadingTask = null;
    pdfDocument = null;
    pdfViewer = null;
    pdfLinkService = null;      // The linkservice allows links to external and internal urls. We don't need it.
                                // When required anyway, find the uncommented pdfLinkService references and uncomment them.
    eventBus = null;
    isCreated = false;
    fnCallbackOnLoading = null;
    _isLoading = false;

    constructor(options) {
        options = options ||{}
        this.fnCallbackOnLoading = options.callbackOnLoading;
    }

    get isLoading() { return this._isLoading;}
    set isLoading(value) { 
        this._isLoading = !!value; 
        if (this.fnCallbackOnLoading) {
            this.fnCallbackOnLoading(this.isLoading);
        }
    }

    ////////////////////////////////////////////////////////////////////////////////////////////////////
    //
    // Public api
    //
    ////////////////////////////////////////////////////////////////////////////////////////////////////
    
    /**
     * Create the viewer on top of the given element. 
     * @param {} element 
     * @returns 
     */
    async create(element) {
        return await this._create(element);
    }

    /**
     * Load an empty document. Basicly blank out the viewer.
     */
    async empty() {
        this.pdfViewer.setDocument(null);
        // await this._loadData(emptyDocument);
    }

    /**
     * Load data either via a function or directly. 
     * When the data is a function - either async or not - we wait for the data to be retrieved and return it.
     * Otherwise, we return the data as is. 
     * NOTE: 
     *       when raw data is provided, it must be 'binary' pdf data. 
     *       For loading base64 data, use loadBase64Data instead. 
     *  
     * @param {*} data 
     * @param {*} filename 
     * @returns 
     */
    async loadData(data, filename /* optional*/) {
        try {
            this.isLoading = true;            
            return await this._loadData(data, filename);
        }
        finally {
            this.isLoading = false;
        }
    }
    // 
    // Load base64 data directly.
    // Note: do not load an empty document via this method as it will assume error and render an 
    //       error document. 
    // Note: the base64 data is supposed to come from a server. 
    //       As such, we assume that the data is base64 encoded on the server. 
    //       For this, we can not use standard atob for decoding back to binary as 
    //       conversion will fail - something with coding points. 
    //       Therefore, we decode it to an array buffer, which works fine with server 
    //       generated base64.
    async loadBase64Data(data, filename /* optional*/) {
        var decodedData = null;
        if (data) {
            decodedData = await string.base64DecodeToArrayBuffer(data);
        }
        if (!decodedData) {
            decodedData = await this._loadErrorData();
        } 
        // We use _loadData directly as we don't need a loading indicator as the data is there already.
        return await this._loadData(decodedData, filename);
    }

    zoomIn() {
        if (this.pdfViewer.currentScale < 2) {
            this.pdfViewer.currentScale += 0.1;
        }
    }
    zoomOut() {
        if (this.pdfViewer.currentScale > 0.5) {
            this.pdfViewer.currentScale -= 0.1;
        }
    }
    maximize() {
        this.pdfViewer.currentScaleValue = "page-width";
    }
    crop() {
        this.pdfViewer.currentScaleValue = "page-fit";
    }
    rotateLeft() {
        this.pdfViewer.pagesRotation = this.pdfViewer.pagesRotation - 90;
    };
    rotateRight() {
        this.pdfViewer.pagesRotation = this.pdfViewer.pagesRotation + 90;
    };
    download(filename) {
        if (!this.pdfDocument) {
            console.log('no pdf document')
            return;
        }
        if (!this.downloadComplete) {
            console.log('not downloadcomplete')
            return;
        }
        var name = string.coalesce(filename, this.currentFileName, 'document.pdf');
        this.pdfDocument.getData().then(
            function getDataSuccess(data) {
                http.downloadRawDataToClient(data, 'application/pdf', name);
            }, () => {}
        );
    };

    /**
     * Closes opened PDF document.
     * Cancels loading and sets document to null.
     */
    async close() {

        if (!this.pdfLoadingTask) {
            return;
        }

        await this.pdfLoadingTask.destroy();
        this.pdfLoadingTask = null;

        if (this.pdfDocument) {
            this.pdfDocument = null;

            this.pdfViewer.setDocument(null);
        }
    }

    ////////////////////////////////////////////////////////////////////////////////////////////////////
    //
    // Private methods
    //
    ////////////////////////////////////////////////////////////////////////////////////////////////////

    /**
     * Load the data for an error document. 
     * @returns 
     */
    async _loadErrorData() {
        let data = pdfErrorDoc.data();
        return data;
    }

    /**
     * When the parameter is a function, wait for it to be retrieved and return it.
     * Otherwise, return the data.
     * 
     * @param {*} data 
     * @returns 
     */
    async _resolveData(data) {
        if (typeof data == 'function') {
            try {
                data = await data();
            }
            catch (e) {
                data = await this._loadErrorData();
            }
        }

        // Yes. I know. The data can be wrapped in multiple data levels because of the http call.
        return data?.data?.data || data?.data || data || null;
    }
    /**
     * Load the data either via a function or directly.
     * When the data is a function - either async or not - we wait for the data to be retrieved and return it.
     * Otherwise, we return the data as is.
     * @param {*} data 
     * @param {*} filename - optinal. When provided is used when downloading the document.
     * @returns 
     */
    async _loadData(data, filename /* optional*/) {
        data = await this._resolveData(data);
        // When no data is provided, show an empty document.
        if (!data) {
            return await this.empty();
        }

        this.currentFileName = filename || "";        
        this.downloadComplete = false;
        // Feed the data as is in the pdf object
        this.pdfLoadingTask = pdfjsLib.getDocument({
            data:data,
            cMapUrl: CDN_CMAP_URL, // cMapUrl,
            standardFontDataUrl: CDN_STANDARD_FONT_DATA_URL,
            cMapPacked: CMAP_PACKED,
        });

        var self = this;

        // Clear the document data first.
        this.pdfViewer.setDocument(null);
//        this.pdfLinkService.setDocument(null);

        return this.pdfLoadingTask.promise.then(
            // Success
            function (pdfDocument) {
                self.pdfDocument = pdfDocument;
                self.pdfViewer.setDocument(pdfDocument);
                self.downloadComplete = true;
//                self.pdfLinkService.setDocument(pdfDocument);
            }, 
            // Error
            function (e) {
                var pdfVersion = `PDF.js v${pdfjsLib.version || "?"} (build: ${pdfjsLib.build || "?"})`;
                console.error(e, pdfVersion);
                self.downloadComplete = true;
            }            
        );
    }

    // ALWAYS await for the create call.
    // The document is emptied when creation is already done. 
    // It is required to await for any loading call - also for loading an empty document. 
    // Therefore, pdfViewer.create(...) without await, following by a loaddata call will fail otherwise.
    async _create(element) {
        // When already created, just empty the document.
        if (this.isCreated) {
            return await this.empty();            
        }

        this.eventBus = new pdfjsViewer.EventBus();
//        this.pdfLinkService = new pdfjsViewer.PDFLinkService({
//            eventBus: this.eventBus,
//        });
        window.pvele = element;
        this.pdfViewer = new pdfjsViewer.PDFViewer({
            container:      element,
            viewer:        element.querySelector(".pdfViewer"),
            eventBus:       this.eventBus,
//             linkService:    this.pdfLinkService,
            annotationMode:       pdfjsLib.AnnotationMode.ENABLE,
//            // Note that when the annotationEdtitorMode is not disabled, loading another 
//            // document in the same instance results in a runtime error. 
//            // As we do not use annotations this seems not problematic but can be researched when
//            // an update of pdjs_dist is installed. 
//            //  
            annotationEditorMode: pdfjsLib.AnnotationEditorType.DISABLE,
            annotationEditorType: pdfjsLib.AnnotationEditorType.NONE,
//             textLayerMode:  1, // TEXT_LAYER_MODE,
textLayerMode: 1, // pdfjsViewer.TextLayerMode.ENABLE_ENHANCE, // 1, // TEXT_LAYER_MODE,
        });

//         this.pdfLinkService.setViewer(this.pdfViewer);

        var self = this;
        this.eventBus.on("pagesinit", function () {
            // We can use pdfViewer now, e.g. let's change default scale.
            self.pdfViewer.currentScaleValue = "page-width";    
            self.pdfViewer.pagesRotation = 0;
        });        
        this.isCreated = true;
    }


}



export default clsPdfjs;